import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Loader2, PartyPopper, Bot, Sparkles, X, Wand2, Star } from "lucide-react";
import { AiAssistant } from "@/components/AiAssistant";
import { getTemplate } from "@/templates/registry";
import { useAuth } from "@/context/AuthContext";
import { VISUAL_STYLE_OPTIONS, BRAND_PERSONALITY_OPTIONS, VISUAL_PREF_OPTIONS, MOOD_BY_INDUSTRY } from "@/lib/designDna";

const PAGE_OPTIONS = ["Home", "About", "Services", "Portfolio", "Blog", "Contact", "Shop", "Events", "Team", "FAQ", "Pricing", "Gallery"];
const FEATURE_OPTIONS = ["Contact form", "Online booking", "E-commerce", "Blog / CMS", "Newsletter signup", "Payment / donations", "Membership area", "Photo gallery", "Video", "Live chat", "Multi-language", "SEO setup"];
const GOAL_OPTIONS = ["Generate leads", "Sell products", "Build credibility", "Grow email list", "Accept donations", "Share information", "Book appointments", "Showcase portfolio"];

// Industry-specific option add-ons — surfaced ONLY when the selected industry matches.
// Add new industries here (keyword match → extra pages/features/goals); the UI wires up automatically.
const INDUSTRY_OPTIONS = {
  church: {
    match: ["church", "ministry", "ministries", "faith", "parish", "congregation", "worship", "gospel", "chapel", "temple"],
    noun: "ministry",
    pages: ["Ministries", "Sermons", "Give", "Prayer Requests", "Bible Study"],
    features: ["Online Sermons (video + audio)", "Livestream Integration", "Online Giving", "Prayer Request Form", "Events Calendar", "Ministry Pages", "Pastor's Welcome", "Bible Study Resources"],
    goals: [],
    mood: MOOD_BY_INDUSTRY.church,
  },
  restaurant: {
    match: ["restaurant", "cafe", "café", "diner", "bakery", "catering", "coffee", "bistro", "eatery", "grill", "pizzeria", "food truck", "food", "bar & grill"],
    noun: "restaurant",
    pages: ["Menu", "Reservations", "Order Online", "Locations", "Specials"],
    features: ["Online Menu", "Online Ordering", "Table Reservations", "Delivery Integration", "Reviews / Testimonials", "Events & Specials"],
    goals: ["Take online orders", "Grow reservations"],
    mood: MOOD_BY_INDUSTRY.restaurant,
  },
  nonprofit: {
    match: ["nonprofit", "non-profit", "non profit", "charity", "foundation", "ngo", "cause", "community org"],
    noun: "organization",
    pages: ["Donate", "Programs", "Volunteer", "Impact", "Events"],
    features: ["Online Donations", "Recurring Giving", "Volunteer Signup", "Event Registration", "Impact Stories", "Donor Portal"],
    goals: ["Raise donations", "Recruit volunteers"],
    mood: MOOD_BY_INDUSTRY.nonprofit,
  },
  contractor: {
    match: ["contractor", "construction", "roofing", "roofer", "plumbing", "plumber", "hvac", "electrician", "electrical", "landscaping", "landscaper", "remodel", "builder", "trades", "painter", "concrete", "handyman", "fencing", "paving"],
    noun: "business",
    pages: ["Services", "Projects / Portfolio", "Service Areas", "Get a Quote", "Reviews"],
    features: ["Free Estimate Form", "Project Gallery / Before & After", "Service Area Map", "Online Booking", "Reviews / Testimonials", "Financing Options"],
    goals: ["Get estimate requests", "Showcase past work"],
    mood: MOOD_BY_INDUSTRY.contractor,
  },
};

const uniq = (arr) => [...new Set(arr)];
const detectIndustryKey = (industry) => {
  const s = (industry || "").toLowerCase();
  for (const [key, cfg] of Object.entries(INDUSTRY_OPTIONS)) {
    if (cfg.match.some((m) => s.includes(m))) return key;
  }
  return null;
};
const ALL_PAGES = uniq([...PAGE_OPTIONS, ...Object.values(INDUSTRY_OPTIONS).flatMap((c) => c.pages)]);
const ALL_GOALS = uniq([...GOAL_OPTIONS, ...Object.values(INDUSTRY_OPTIONS).flatMap((c) => c.goals)]);
const ALL_FEATURES = uniq([...FEATURE_OPTIONS, ...Object.values(INDUSTRY_OPTIONS).flatMap((c) => c.features)]);
const ALL_MOODS = uniq(Object.values(MOOD_BY_INDUSTRY).flat());
const BUDGETS = ["Under $1,000", "$1,000 - $2,500", "$2,500 - $5,000", "$5,000 - $10,000", "$10,000+"];
const TIMELINES = ["ASAP (1-2 weeks)", "3-4 weeks", "1-2 months", "2-3 months", "Flexible"];

// Estimated investment range — reframes a single price as a feature-justified range.
const estRange = (price) => {
  const p = Number(price);
  if (!p) return "Custom Quote";
  const hi = Math.round((p * 1.5) / 5) * 5;
  return `$${p.toLocaleString()} – $${hi.toLocaleString()}`;
};

// Upper bound of a stated budget bracket (e.g. "$1,000 - $2,500" -> 2500; "$10,000+" -> Infinity).
const budgetCeiling = (b) => {
  if (!b || /\+/.test(b)) return Infinity;
  const nums = (b.match(/[\d,]+/g) || []).map((n) => Number(n.replace(/,/g, "")));
  return nums.length ? Math.max(...nums) : Infinity;
};

const steps = ["Business", "Vision", "Website", "Design", "Content", "Budget", "Contact"];

// Section completeness for the post-Fill summary. Each returns true when the
// section has enough for the assessment (the user can still edit anything).
const FILL_SECTIONS = [
  { name: "Business", idx: 0, done: (f) => !!f.org_name && !!f.industry },
  { name: "Mission & Vision", idx: 1, done: (f) => !!f.mission && !!f.vision && !!f.target_audience },
  { name: "Website", idx: 2, done: (f) => (f.goals?.length || 0) > 0 && (f.pages?.length || 0) > 0 },
  { name: "Design", idx: 3, done: (f) => (f.visual_style?.length || 0) > 0 && (f.brand_personality?.length || 0) > 0 },
  { name: "Content & Assets", idx: 4, done: (f) => !!f.has_logo && !!f.has_photos && !!f.has_videos && !!f.has_copy },
  { name: "Budget & Timeline", idx: 5, done: (f) => !!f.budget && !!f.timeline },
  { name: "Contact", idx: 6, done: (f) => !!f.contact_name && !!f.email },
];
const computeFillSummary = (f) => {
  const checks = [
    !!f.org_name, !!f.industry, !!f.mission, !!f.vision, !!f.target_audience,
    (f.goals?.length || 0) > 0, (f.pages?.length || 0) > 0,
    (f.visual_style?.length || 0) > 0, (f.brand_personality?.length || 0) > 0, !!f.colors,
    !!f.has_logo, !!f.has_photos, !!f.has_videos, !!f.has_copy,
    !!f.budget, !!f.timeline, !!f.contact_name, !!f.email,
  ];
  const percent = Math.round((checks.filter(Boolean).length / checks.length) * 100);
  const complete = FILL_SECTIONS.filter((s) => s.done(f)).length;
  const review = FILL_SECTIONS.filter((s) => !s.done(f)).map((s) => ({ name: s.name, idx: s.idx }));
  return { percent, complete, total: FILL_SECTIONS.length, review };
};

export default function Questionnaire() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setSession } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [miPlan, setMiPlan] = useState(null);
  const [miLoading, setMiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  useEffect(() => {
    document.body.classList.toggle("concierge-drawer-open", aiOpen);
    return () => document.body.classList.remove("concierge-drawer-open");
  }, [aiOpen]);
  const [sessionId, setSessionId] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [quickBusy, setQuickBusy] = useState(false);
  const [dna, setDna] = useState(null);
  const [dnaLoaded, setDnaLoaded] = useState(false);
  const [prefilledCount, setPrefilledCount] = useState(0);
  const [fillSummary, setFillSummary] = useState(null);

  const [f, setF] = useState({
    org_name: "", industry: "", mission: "", vision: "", tagline: "", target_audience: "",
    goals: [], pages: [], features: [], colors: "", has_logo: "", has_photos: "", has_videos: "", has_copy: "",
    websites_liked: "", budget: "", timeline: "", details: "",
    visual_style: [], brand_personality: [], visual_preferences: [], design_mood: [], design_feeling: "",
    selected_design: getTemplate(params.get("template"))?.name || params.get("design") || "",
    selected_template: params.get("template") || "",
    package_name: params.get("package") || "",
    contact_name: "", email: "", phone: "", address: "", prior_service: null, service_status: "",
    confirmed_facts: { ownership: "", years_experience: "", licensed: "", insured: "", warranty: "", certifications: "", business_hours: "", emergency_service: "", free_estimates: "", financing: "", service_areas: "" },
  });

  // Draft autosave — keeps a visitor's reviewed answers safe across refresh/close until Submit.
  const hydrated = useRef(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("atf_assessment_draft");
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.f) setF((p) => ({ ...p, ...saved.f }));
        if (typeof saved?.step === "number") setStep(saved.step);
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!hydrated.current) { hydrated.current = true; return; }
    try { localStorage.setItem("atf_assessment_draft", JSON.stringify({ f, step })); } catch (e) {}
  }, [f, step]);

  useEffect(() => {
    const ind = params.get("industry");
    if (ind) setF((p) => ({ ...p, industry: p.industry || ind }));
    // Returning client? Pre-fill from their Business DNA (editable, with provenance).
    api.get("/portal/business-dna").then((r) => {
      const d = r.data;
      if (!d?.loaded) return;
      setDna(d); setDnaLoaded(true);
      const q = d.questionnaire || {};
      setF((p) => {
        const n = { ...p };
        const setIf = (k, v) => { if (v && !String(n[k] || "").trim()) n[k] = v; };
        setIf("org_name", q.org_name?.value);
        setIf("industry", q.industry?.value);
        setIf("tagline", q.tagline?.value);
        setIf("mission", q.mission?.value);
        setIf("vision", q.vision?.value);
        setIf("target_audience", q.target_audience?.value);
        setIf("colors", q.colors?.value);
        setIf("address", q.address?.value);
        if (Array.isArray(q.goals?.value) && n.goals.length === 0) n.goals = q.goals.value.filter((g) => ALL_GOALS.includes(g));
        if (Array.isArray(q.pages?.value) && n.pages.length === 0) n.pages = q.pages.value.filter((g) => ALL_PAGES.includes(g));
        return n;
      });
      const cnt = Object.values(q).filter((x) => x && (Array.isArray(x.value) ? x.value.length : x.value)).length;
      setPrefilledCount(cnt);
      if (cnt) toast.success(`Pre-filled ${cnt} field(s) from your Business Profile — review and edit anything.`);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  // Arriving from the AI Assistant's "Start Project" bridge — auto-fill from that chat.
  useEffect(() => {
    const fromChat = params.get("from_chat");
    if (fromChat) { setSessionId(fromChat); applyFromChat(fromChat); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCF = (k, v) => setF((p) => ({ ...p, confirmed_facts: { ...p.confirmed_facts, [k]: v } }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  // Industry-aware option sets (e.g. Church surfaces Ministries/Sermons/Give/Livestream…).
  const indKey = detectIndustryKey(f.industry);
  const ind = indKey ? INDUSTRY_OPTIONS[indKey] : null;
  const orgNoun = ind?.noun || "business";
  const goalOptions = ind ? uniq([...GOAL_OPTIONS, ...ind.goals]) : GOAL_OPTIONS;
  const pageOptions = ind ? uniq([...PAGE_OPTIONS, ...ind.pages]) : PAGE_OPTIONS;
  const featureOptions = ind ? uniq([...FEATURE_OPTIONS, ...ind.features]) : FEATURE_OPTIONS;

  const canNext = useMemo(() => {
    if (step === 0) return f.org_name.trim().length > 1;
    if (step === 6) return f.contact_name.trim() && /\S+@\S+\.\S+/.test(f.email);
    return true;
  }, [step, f]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/client-requests", f);
      setDone(data);
      try { localStorage.removeItem("atf_assessment_draft"); } catch (e) {}
      toast.success("Your project request was received!");
      if (data.project_id) pollMissionPlan(data.project_id, data.auto_login?.token);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Save My Assessment -> frictionless portal entry. Already signed in? Go straight in.
  // Brand-new/unclaimed visitor? The submit response carries a one-shot login token, so we
  // set the session and drop them into the portal with no login wall. A CLAIMED account
  // (no token returned) is routed to sign in, with their email pre-filled.
  const saveAssessment = () => {
    if (user) { navigate("/portal?view=plan"); return; }
    const al = done?.auto_login;
    if (al?.token) {
      setSession(al.token, al.user);
      toast.success("Your workspace is ready — here's your full plan.");
      navigate("/portal?view=plan");
      return;
    }
    toast.info("You already have an account — please sign in to save your assessment.");
    navigate(`/login?email=${encodeURIComponent(f.email || "")}`);
  };

  // Mission Intelligence builds + persists a versioned Mission Plan server-side (background).
  // We poll the versioned MI HTTP API for the saved plan — validating the external-ready
  // /api/mi/v1 surface while keeping MI fully decoupled from the marketplace.
  const pollMissionPlan = async (projectId, token) => {
    setMiLoading(true);
    const started = Date.now();
    // Fresh public submitters aren't logged in yet on this screen, so authenticate the
    // poll with the one-shot token from submit (their own project) — no global session set.
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const attempt = async () => {
      try {
        const { data } = await api.get(`/mi/v1/projects/${projectId}/mission-plans/latest`, cfg);
        if (data && data.document && (data.document.action_plan?.length || data.document.executive_summary)) {
          setMiPlan(data);
          setMiLoading(false);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (Date.now() - started < 60000) {
        setTimeout(attempt, 2500);
      } else {
        setMiLoading(false); // give up quietly — MI is a non-critical enhancement
      }
    };
    attempt();
  };

  // Guided Quick Setup from the concierge — chips map 1:1 to the form (instant, accurate);
  // the free-text description is AI-extracted only for the descriptive fields.
  const handleQuickSetup = async (sel) => {
    setQuickBusy(true);
    try {
      setF((p) => {
        const next = { ...p };
        // An explicit industry pick in the concierge is authoritative — it always wins,
        // and switching to a DIFFERENT industry starts its option sets fresh (so a Church's
        // Sermons/Give don't linger when the visitor switches to, say, Restaurant).
        const industryChanged = !!sel.industryLabel &&
          sel.industryLabel.trim().toLowerCase() !== String(p.industry || "").trim().toLowerCase();
        if (sel.industryLabel) next.industry = sel.industryLabel;
        const base = (k) => (industryChanged ? [] : (p[k] || []));
        const mergeArr = (k, vals) => { next[k] = Array.from(new Set([...base(k), ...((vals || []))])); };
        mergeArr("goals", sel.goals);
        mergeArr("pages", sel.pages);
        mergeArr("features", sel.features);
        mergeArr("visual_style", sel.visualStyle);
        mergeArr("brand_personality", sel.brandPersonality);
        mergeArr("design_mood", sel.mood);
        mergeArr("visual_preferences", sel.visualPref);
        if (sel.budget && (industryChanged || !p.budget)) next.budget = sel.budget;
        else if (industryChanged && !sel.budget) next.budget = "";
        if (sel.timeline && (industryChanged || !p.timeline)) next.timeline = sel.timeline;
        else if (industryChanged && !sel.timeline) next.timeline = "";
        return next;
      });
      let aiCount = 0;
      if (sel.description && sel.description.trim()) {
        try {
          const { data } = await api.post("/assistant/extract", {
            scope: "public", visitor_id: localStorage.getItem("atf_visitor_id"), text: sel.description.trim(),
          });
          const fields = data.fields || {};
          setF((p) => {
            const next = { ...p };
            for (const [k, v] of Object.entries(fields)) {
              if (Array.isArray(v)) next[k] = Array.from(new Set([...(p[k] || []), ...v]));
              else if (v && !String(p[k] || "").trim()) next[k] = v;
            }
            return next;
          });
          aiCount = Object.keys(fields).length;
        } catch (e) { /* non-blocking — chips are already applied */ }
      }
      const chipCount = (sel.goals?.length || 0) + (sel.pages?.length || 0) + (sel.features?.length || 0) + (sel.visualStyle?.length || 0) + (sel.brandPersonality?.length || 0) + (sel.mood?.length || 0) + (sel.visualPref?.length || 0) + (sel.budget ? 1 : 0) + (sel.timeline ? 1 : 0);
      toast.success(`Prefilled ${chipCount + aiCount} field(s) — review and edit anything.`);
      setAiOpen(false);
      window.scrollTo(0, 0);
    } finally {
      setQuickBusy(false);
    }
  };

  const applyFromChat = async (sidArg) => {
    const sid = sidArg || sessionId;
    if (!sid) {
      toast.info("Chat with the concierge first, then I'll fill in your details.");
      return;
    }
    setExtracting(true);
    try {
      const doExtract = async () => {
        const { data } = await api.post("/assistant/extract", {
          session_id: sid, scope: "public",
          visitor_id: localStorage.getItem("atf_visitor_id"),
          allowed: {
            goals: ALL_GOALS, pages: ALL_PAGES, features: ALL_FEATURES,
            visual_style: VISUAL_STYLE_OPTIONS, brand_personality: BRAND_PERSONALITY_OPTIONS,
            visual_preferences: VISUAL_PREF_OPTIONS, design_mood: ALL_MOODS,
          },
        });
        return data;
      };
      let data = await doExtract();
      // The extractor uses AI; on a transient hiccup it returns ai_connected:false.
      // Retry once before telling the user anything so a glitch never looks like "no info".
      if (data.ai_connected === false) {
        await new Promise((r) => setTimeout(r, 1200));
        data = await doExtract();
      }
      if (data.ai_connected === false) {
        toast.error("I had trouble reading our conversation just now — please tap the button again in a moment. Your chat is saved.");
        return;
      }
      const fields = data.fields || {};
      if (Object.keys(fields).length === 0) {
        toast.info("Tell the concierge a little more about your business, then try again.");
        return;
      }
      const merged = { ...f };
      for (const [k, v] of Object.entries(fields)) {
        if (k === "confirmed_facts" && v && typeof v === "object") {
          const cf = { ...(f.confirmed_facts || {}) };
          for (const [ck, cv] of Object.entries(v)) {
            if (cv && !String(cf[ck] || "").trim()) cf[ck] = cv;
          }
          merged.confirmed_facts = cf;
        } else if (Array.isArray(v)) merged[k] = Array.from(new Set([...(f[k] || []), ...v]));
        else if (v && !String(f[k] || "").trim()) merged[k] = v;
      }
      setF(merged);
      setFillSummary(computeFillSummary(merged));
      setStep(0);
      setAiOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  };

  const pickAlternate = async () => {
    const newRec = done.alternate_package;
    if (!newRec) return;
    const newAlt = done.recommended_package;
    setDone({ ...done, recommended_package: { ...newRec }, alternate_package: newAlt });
    try {
      const { data } = await api.post(`/client-requests/${done.request_id}/select-package`, { package_id: newRec.id });
      if (data?.recommended_package) setDone((d) => ({ ...d, recommended_package: data.recommended_package }));
      toast.success(`Switched to ${newRec.name}`);
    } catch (e) { /* local swap stays even if persist fails */ }
  };

  if (done) {
    const rec = done.recommended_package;
    const tpls = done.recommended_templates || [];
    const arr = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
    const glance = [
      ["Industry", f.industry],
      ["Vision", f.vision],
      ["Audience", f.target_audience],
      ["Goals", arr(f.goals).join(" · ")],
      ["Pages", arr(f.pages).join(" · ")],
      ["Key features", arr(f.features).join(" · ")],
      ["Budget", f.budget],
      ["Timeline", f.timeline],
    ].filter(([, v]) => v && String(v).trim());
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 md:py-28" data-testid="questionnaire-success">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto border border-gold flex items-center justify-center text-gold mb-8"><PartyPopper size={28} /></div>
          <h1 className="font-heading text-4xl text-white mb-4">Here's your free assessment, {f.contact_name.split(" ")[0] || "friend"}!</h1>
          <p className="text-gray-400 text-lg mb-10">Your instant assessment for <span className="text-gold">{f.org_name}</span> is ready below — free, no strings attached. It's yours to keep. When you're ready to save it and get your full plan, you can create a free account (no commitment).</p>
        </div>

        {glance.length > 0 && (
          <div className="glass p-6 md:p-8 mb-6 text-left" data-testid="success-summary">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4 flex items-center gap-2"><Sparkles size={13} /> Your project at a glance</div>
            <dl className="divide-y divide-white/5">
              {glance.map(([label, value]) => (
                <div key={label} className="flex flex-col sm:flex-row sm:gap-4 py-2.5">
                  <dt className="text-gray-500 text-sm sm:w-40 shrink-0">{label}</dt>
                  <dd className="text-gray-200 text-sm leading-relaxed">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {rec && (
          <div className="glass p-6 md:p-8 mb-6 text-left" data-testid="success-package">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Recommended for you</div>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
              <h3 className="font-heading text-2xl text-white">{rec.name}</h3>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-gray-500">Estimated investment</div>
                <div className="text-gold font-heading text-xl" data-testid="success-price-range">{estRange(rec.price)}</div>
              </div>
            </div>
            {rec.tagline && <p className="text-gray-400 mb-1">{rec.tagline}</p>}
            <p className="text-xs text-gray-500 mb-4">Your final quote is tailored to the exact features you choose — this range reflects what you selected.</p>
            {rec.match_reason && (
              <div className="flex items-start gap-2 text-sm text-gold/90 bg-gold/10 border border-gold/20 px-3 py-2 mb-4" data-testid="success-package-reason">
                <Sparkles size={14} className="text-gold mt-0.5 shrink-0" /> {rec.match_reason}
              </div>
            )}
            {Number(rec.price) > budgetCeiling(f.budget) && (
              <div className="flex items-start gap-2 text-sm text-amber-300/90 bg-amber-400/10 border border-amber-400/25 px-3 py-2 mb-4" data-testid="success-over-budget">
                <Sparkles size={14} className="text-amber-300 mt-0.5 shrink-0" /> Based on your selections, this is a bit above your stated budget{f.budget ? ` (${f.budget})` : ""} — we can tailor the scope to fit. Your deposit and final quote adjust to the exact features you choose.
              </div>
            )}
            {done.alternate_package && (
              <div className="border border-white/10 bg-navy-800 px-4 py-3 mb-4" data-testid="success-alternate-package">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Also fits your budget</div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-white font-heading">{done.alternate_package.name}</span>
                  <span className="text-gold text-sm">{estRange(done.alternate_package.price)}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">A leaner starting point you can launch now and grow from — same quality, fewer features to begin.</p>
                <button onClick={pickAlternate} data-testid="pick-alternate-btn" className="mt-3 text-xs font-semibold border border-gold/50 text-gold px-3 py-1.5 hover:bg-gold/10 transition-colors">
                  Pick {done.alternate_package.name} instead →
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-400 mb-4">
              <span>{Number(rec.deposit) > 0
                ? <>Deposit to start: <span className="text-white">${Number(rec.deposit).toLocaleString()}</span></>
                : <span className="text-white">No deposit to begin</span>}</span>
              <span>Timeline: <span className="text-white">{rec.timeline}</span></span>
            </div>
            {(arr(f.features).length > 0 || rec.deliverables?.length > 0) && (() => {
              const requested = arr(f.features);
              const useReq = requested.length > 0;
              const items = useReq ? requested : rec.deliverables;
              return (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2" data-testid="plan-includes-label">{useReq ? "What you asked for" : `What your ${rec.name} plan can include`}</div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {items.map((d) => (
                      <li key={d} className="flex items-start gap-2 text-sm text-gray-300"><Check size={14} className="text-gold mt-0.5 shrink-0" /> {d}</li>
                    ))}
                  </ul>
                </>
              );
            })()}
          </div>
        )}

        {tpls.length > 0 && (
          <div className="mb-8 text-left" data-testid="success-templates">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Designs picked for you</div>
            <div className="grid sm:grid-cols-3 gap-4">
              {tpls.map((t) => (
                <Link key={t.id} to={`/template/${t.slug || t.id}`} data-testid={`success-template-${t.id}`}
                  className="group block bg-navy-800 border border-white/10 hover:border-gold/50 transition-colors overflow-hidden">
                  <div className="aspect-[4/3] bg-navy overflow-hidden">
                    {t.preview_image
                      ? <img src={t.preview_image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">{t.category}</div>}
                  </div>
                  <div className="p-3">
                    <div className="text-white text-sm font-medium truncate">{t.title}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-gold text-xs uppercase tracking-wide">{t.category || "Design"}</span>
                      <span className="flex items-center gap-1 text-gray-400 text-xs"><Star size={11} className="text-gold fill-gold" /> {t.rating}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {tpls.length === 0 && (
          <div className="glass p-6 md:p-8 mb-8 text-left" data-testid="success-custom-build">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Your design</div>
            <h3 className="font-heading text-2xl text-white mb-2">We'll craft a custom design for you</h3>
            <p className="text-gray-400">Your business is a great fit for a <span className="text-white">custom{f.industry ? ` ${f.industry}` : ""} website</span> built around your goals — not a template. Ask Top Foster will design it from your brief, and it'll be uniquely yours.</p>
          </div>
        )}


        {(miLoading || miPlan?.document) && (
          <div className="glass p-6 md:p-8 mb-8 text-left" data-testid="success-mission-plan">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2 flex items-center gap-2"><Bot size={13} /> Your Mission Plan{miPlan?.version ? ` · v${miPlan.version}` : ""}</div>
            {miLoading ? (
              <div className="flex items-center gap-3 text-gray-400 py-6" data-testid="mission-plan-loading">
                <Loader2 className="animate-spin text-gold" size={18} />
                <span className="text-sm">Mission Intelligence is assembling your strategists…</span>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-2xl text-white mb-2">{miPlan.document.headline}</h3>
                {miPlan.document.overview && (
                  <p className="text-gray-400 mb-5">{miPlan.document.overview}</p>
                )}
                <ol className="space-y-4" data-testid="mission-action-plan">
                  {miPlan.document.action_plan.map((a, i) => (
                    <li key={i} className="flex gap-3" data-testid={`mission-action-${i}`}>
                      <span className="shrink-0 w-6 h-6 flex items-center justify-center border border-gold/40 text-gold text-xs font-heading">{i + 1}</span>
                      <div>
                        <div className="text-white text-sm font-medium">{a.title}</div>
                        {a.detail && <div className="text-gray-400 text-sm mt-0.5">{a.detail}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
                {miPlan.document.strategists_consulted?.length > 0 && (
                  <p className="text-[11px] text-gray-500 mt-5 flex items-center gap-1.5">
                    <Sparkles size={11} className="text-gold" /> Guided by Mission Intelligence · {miPlan.document.strategists_consulted.join(", ")}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex gap-4 justify-center flex-wrap">
          <button onClick={saveAssessment} className="bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-colors" data-testid="success-portal-btn">Save My Assessment & Get Full Plan</button>
          <Link to="/book" className="border border-white/25 text-white px-8 py-4 hover:bg-white/5 transition-colors" data-testid="success-consult-btn">Book a Free Consultation</Link>
        </div>
        <p className="text-center text-xs text-gray-500 mt-4">Creating an account is free and optional — it just lets us save your assessment and build your full plan. <Link to="/marketplace" className="text-gold/90 hover:text-gold hover:underline">Or browse all designs →</Link></p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
      <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Business Assessment</div>
      <h1 className="font-heading text-4xl text-white mb-4">Tell us about your {orgNoun}{orgNoun === "business" ? ", organization or idea" : ""}</h1>

      {/* Persistent AI-Fill summary — replaces the disappearing toast so the user can study it */}
      {fillSummary && (
        <div className="relative bg-navy-800 border border-gold/30 px-5 py-4 mb-4" data-testid="q-fill-summary">
          <button onClick={() => setFillSummary(null)} data-testid="q-fill-summary-dismiss"
            className="absolute top-2.5 right-2.5 text-gray-500 hover:text-white transition-colors"><X size={16} /></button>
          <div className="flex items-start gap-3 pr-6">
            <Sparkles className="text-gold shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-gray-300 space-y-1">
              <div className="text-white font-medium">AI Concierge filled {fillSummary.percent}% of your assessment.</div>
              <div>{fillSummary.complete} of {fillSummary.total} sections are complete.</div>
              {fillSummary.review.length > 0 ? (
                <div>
                  {fillSummary.review.length} section{fillSummary.review.length === 1 ? "" : "s"} need{fillSummary.review.length === 1 ? "s" : ""} your review:{" "}
                  {fillSummary.review.map((r, i) => (
                    <span key={r.idx}>
                      <button onClick={() => setStep(r.idx)} className="text-gold underline hover:text-gold-light" data-testid={`q-fill-review-${r.idx}`}>{r.name}</button>
                      {i < fillSummary.review.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-gold">All required fields are complete — nice work! (Optional fields like a tagline can be left blank.)</div>
              )}
              <div className="text-gray-400">Please review all answers before submitting.</div>
            </div>
          </div>
        </div>
      )}

      {/* Business DNA pre-fill banner — only when we actually pre-filled something */}
      {dnaLoaded && dna && prefilledCount > 0 && (
        <div className="flex items-center gap-3 bg-navy-800 border border-gold/25 px-4 py-3 mb-4" data-testid="q-dna-banner">
          <Sparkles className="text-gold shrink-0" size={16} />
          <div className="flex-1 text-sm text-gray-300">We pre-filled {prefilledCount} field{prefilledCount === 1 ? "" : "s"} from your <b className="text-white">Business Profile</b> ({dna.knowledge.score}% complete). Review and edit anything.</div>
        </div>
      )}

      {/* AI Concierge banner */}
      <button onClick={() => setAiOpen(true)} data-testid="open-concierge-banner"
        className="w-full flex items-center gap-3 text-left bg-gold/10 border border-gold/25 hover:border-gold/50 transition-colors px-4 py-3 mb-8 group">
        <span className="w-9 h-9 shrink-0 flex items-center justify-center border border-gold/40 text-gold"><Bot size={18} /></span>
        <span className="flex-1">
          <span className="block text-white text-sm font-medium">Not sure what to write? Let Top Foster guide you.</span>
          <span className="block text-gray-400 text-xs">Top Foster is your AI guide — tell it about your business or organization in your own words, and it'll help complete your assessment.</span>
        </span>
        <span className="hidden sm:flex items-center gap-1.5 text-gold text-sm font-medium group-hover:gap-2.5 transition-all"><Sparkles size={14} /> Open</span>
      </button>

      {/* progress — click any step to jump back/forward for review */}
      <div className="flex items-center gap-2 mb-3" data-testid="progress-steps">
        {steps.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(i)} title={`Go to: ${s}`} aria-label={`Go to ${s}`} data-testid={`progress-step-${i}`}
            className={`h-1.5 flex-1 transition-colors cursor-pointer hover:opacity-80 ${i <= step ? "bg-gold" : "bg-white/10"}`} />
        ))}
      </div>
      <div className="flex justify-between text-sm text-gray-400 mb-10">
        <span>Step {step + 1} of {steps.length}</span>
        <span className="text-gold">{steps[step]}</span>
      </div>

      <div className="glass p-8 md:p-10 min-h-[360px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <StepWrap title="Your business or organization">
                <Text label="Organization / business name *" hint={<DnaChip src={dna?.questionnaire?.org_name?.source} />} value={f.org_name} onChange={(v) => set("org_name", v)} testid="q-org" placeholder="e.g. Grace Community Church" />
                <Text label="Industry" hint={<DnaChip src={dna?.questionnaire?.industry?.source} />} value={f.industry} onChange={(v) => set("industry", v)} testid="q-industry" placeholder="e.g. Nonprofit, Restaurant, Consulting" />
                <Text label="Tagline / one-liner" hint={<DnaChip src={dna?.questionnaire?.tagline?.source} />} value={f.tagline} onChange={(v) => set("tagline", v)} testid="q-tagline" placeholder="A short phrase that describes you" />
              </StepWrap>
            )}
            {step === 1 && (
              <StepWrap title="Mission, vision & audience">
                <Area label="Mission" hint={<DnaChip src={dna?.questionnaire?.mission?.source} />} value={f.mission} onChange={(v) => set("mission", v)} testid="q-mission" placeholder="What do you do and why?" />
                <Area label="Vision" hint={<DnaChip src={dna?.questionnaire?.vision?.source} />} value={f.vision} onChange={(v) => set("vision", v)} testid="q-vision" placeholder="Where are you headed?" />
                <Text label="Target audience" hint={<DnaChip src={dna?.questionnaire?.target_audience?.source} />} value={f.target_audience} onChange={(v) => set("target_audience", v)} testid="q-audience" placeholder="Who are you trying to reach?" />
              </StepWrap>
            )}
            {step === 2 && (
              <StepWrap title="Website goals, pages & features">
                {ind && (
                  <div className="flex items-center gap-2 text-xs text-gold/90 bg-gold/10 border border-gold/25 px-3 py-2 -mt-1" data-testid="q-industry-hint">
                    <Sparkles size={13} className="shrink-0" /> We've added {indKey}-specific options tailored to your {orgNoun}.
                  </div>
                )}
                <ChipGroup label="Website goals" options={goalOptions} selected={f.goals} onToggle={(v) => toggle("goals", v)} testid="q-goals" />
                <ChipGroup label="Required pages" options={pageOptions} selected={f.pages} onToggle={(v) => toggle("pages", v)} testid="q-pages" />
                <ChipGroup label="Desired features" options={featureOptions} selected={f.features} onToggle={(v) => toggle("features", v)} testid="q-features" />
              </StepWrap>
            )}
            {step === 3 && (
              <StepWrap title="Look & feel — your design style">
                <p className="text-sm text-gray-400 -mt-2">This becomes part of your Business Profile — it guides the design, colors, typography and photography we create for you.</p>
                <ChipGroup label="Visual style (choose up to 5)" options={VISUAL_STYLE_OPTIONS} selected={f.visual_style} onToggle={(v) => toggle("visual_style", v)} testid="q-visual-style" max={5} />
                <ChipGroup label="Brand personality" options={BRAND_PERSONALITY_OPTIONS} selected={f.brand_personality} onToggle={(v) => toggle("brand_personality", v)} testid="q-brand-personality" />
                <ChipGroup label="Visual preferences" options={VISUAL_PREF_OPTIONS} selected={f.visual_preferences} onToggle={(v) => toggle("visual_preferences", v)} testid="q-visual-prefs" />
                {ind?.mood && (
                  <ChipGroup label={`${indKey.charAt(0).toUpperCase() + indKey.slice(1)} mood`} options={ind.mood} selected={f.design_mood} onToggle={(v) => toggle("design_mood", v)} testid="q-design-mood" />
                )}
                <Area label="Describe the feeling visitors should have when they land on your website" value={f.design_feeling} onChange={(v) => set("design_feeling", v)} testid="q-design-feeling" placeholder="e.g. Warm and welcoming, immediately invited, informed and encouraged — inspiring without distractions." />
                <Text label="Brand colors" hint={<DnaChip src={dna?.questionnaire?.colors?.source} />} value={f.colors} onChange={(v) => set("colors", v)} testid="q-colors" placeholder="e.g. Navy & gold, or hex codes" />
                <Area label="Websites you like (and why)" value={f.websites_liked} onChange={(v) => set("websites_liked", v)} testid="q-liked" placeholder="Paste links or describe styles you admire" />
                {f.selected_design && <p className="text-sm text-gold">Starting from design: {f.selected_design}</p>}
                {f.package_name && <p className="text-sm text-gold">Selected package: {f.package_name}</p>}
              </StepWrap>
            )}
            {step === 4 && (
              <StepWrap title="Content & assets">
                <Select label="Do you have a logo?" value={f.has_logo} onChange={(v) => set("has_logo", v)} testid="q-logo" options={["Yes, ready", "Need one designed", "Not sure"]} />
                <Select label="Do you have photos?" value={f.has_photos} onChange={(v) => set("has_photos", v)} testid="q-photos" options={["Yes", "Some", "Need stock photos"]} />
                <Select label="Do you have video?" value={f.has_videos} onChange={(v) => set("has_videos", v)} testid="q-videos" options={["Yes", "No", "Maybe later"]} />
                <Select label="Do you have written content / copy?" value={f.has_copy} onChange={(v) => set("has_copy", v)} testid="q-copy" options={["Yes, ready", "Draft only", "Need copywriting"]} />

                <div className="pt-6 mt-2 border-t border-white/10" data-testid="trust-facts-section">
                  <h4 className="font-heading text-lg text-white mb-1">Trust & credentials <span className="text-gray-500 text-sm font-normal">(optional)</span></h4>
                  <p className="text-xs text-gray-400 mb-4">Only fill in what's true — these become confirmed facts we can state on your site. <span className="text-gold">Leaving one blank means we simply won't claim it</span> (we never make things up).</p>
                  <div className="grid sm:grid-cols-2 gap-x-5">
                    <Text label="Ownership" value={f.confirmed_facts.ownership} onChange={(v) => setCF("ownership", v)} testid="cf-ownership" placeholder="e.g. Family-owned, Veteran-owned" />
                    <Text label="Years in business / experience" value={f.confirmed_facts.years_experience} onChange={(v) => setCF("years_experience", v)} testid="cf-years" placeholder="e.g. 20+ years combined" />
                    <Text label="Licensed?" value={f.confirmed_facts.licensed} onChange={(v) => setCF("licensed", v)} testid="cf-licensed" placeholder="e.g. Yes — License #AL-12345" />
                    <Text label="Insured?" value={f.confirmed_facts.insured} onChange={(v) => setCF("insured", v)} testid="cf-insured" placeholder="e.g. Yes, fully insured" />
                    <Text label="Warranty / guarantee" value={f.confirmed_facts.warranty} onChange={(v) => setCF("warranty", v)} testid="cf-warranty" placeholder="e.g. 1-year workmanship warranty" />
                    <Text label="Certifications" value={f.confirmed_facts.certifications} onChange={(v) => setCF("certifications", v)} testid="cf-certs" placeholder="e.g. EPA certified" />
                    <Text label="Business hours" value={f.confirmed_facts.business_hours} onChange={(v) => setCF("business_hours", v)} testid="cf-hours" placeholder="e.g. Mon–Fri 8–5" />
                    <Text label="Emergency / same-day service?" value={f.confirmed_facts.emergency_service} onChange={(v) => setCF("emergency_service", v)} testid="cf-emergency" placeholder="e.g. Yes, 24/7 emergency" />
                    <Text label="Free estimates?" value={f.confirmed_facts.free_estimates} onChange={(v) => setCF("free_estimates", v)} testid="cf-estimates" placeholder="e.g. Free estimates for installs" />
                    <Text label="Financing available?" value={f.confirmed_facts.financing} onChange={(v) => setCF("financing", v)} testid="cf-financing" placeholder="e.g. Financing available" />
                    <Text label="Service areas" value={f.confirmed_facts.service_areas} onChange={(v) => setCF("service_areas", v)} testid="cf-areas" placeholder="e.g. Montgomery, Prattville, Wetumpka" />
                  </div>
                </div>
              </StepWrap>
            )}
            {step === 5 && (
              <StepWrap title="Budget & timeline">
                <Select label="Budget range" value={f.budget} onChange={(v) => set("budget", v)} testid="q-budget" options={BUDGETS} />
                <Select label="Desired timeline" value={f.timeline} onChange={(v) => set("timeline", v)} testid="q-timeline" options={TIMELINES} />
                <Area label="Special requirements / anything else" value={f.details} onChange={(v) => set("details", v)} testid="q-details" placeholder="Integrations, deadlines, accessibility needs, etc." />
              </StepWrap>
            )}
            {step === 6 && (
              <StepWrap title="How can we reach you?">
                <Text label="Full name *" value={f.contact_name} onChange={(v) => set("contact_name", v)} testid="q-name" placeholder="Your name" />
                <Text label="Email *" value={f.email} onChange={(v) => set("email", v)} testid="q-email" placeholder="you@company.com" type="email" />
                <Text label="Phone" value={f.phone} onChange={(v) => set("phone", v)} testid="q-phone" placeholder="Optional" />
                <Text label="Address / location" value={f.address} onChange={(v) => set("address", v)} testid="q-address" placeholder="Optional" />
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Prior military service?</label>
                  <div className="flex gap-2" data-testid="q-prior-service">
                    <button type="button" onClick={() => { set("prior_service", true); }} data-testid="q-prior-yes"
                      className={`px-6 py-2.5 text-sm border transition-colors ${f.prior_service === true ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>Yes</button>
                    <button type="button" onClick={() => { set("prior_service", false); set("service_status", ""); }} data-testid="q-prior-no"
                      className={`px-6 py-2.5 text-sm border transition-colors ${f.prior_service === false ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>No</button>
                  </div>
                  {f.prior_service === true && (
                    <div className="mt-3" data-testid="q-service-status">
                      <label className="block text-sm text-gray-400 mb-2">Which best describes you?</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button type="button" onClick={() => set("service_status", "transitioning")} data-testid="q-status-transitioning"
                          className={`px-5 py-2.5 text-sm border transition-colors ${f.service_status === "transitioning" ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>Currently serving / transitioning</button>
                        <button type="button" onClick={() => set("service_status", "veteran")} data-testid="q-status-veteran"
                          className={`px-5 py-2.5 text-sm border transition-colors ${f.service_status === "veteran" ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>Already separated / retired</button>
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-gray-500 mt-1.5">Veterans may qualify for our Veterans Launch Initiative &amp; a veteran appreciation discount.</p>
                </div>
              </StepWrap>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="q-back"
          className="flex items-center gap-2 px-6 py-3 border border-white/20 text-white hover:bg-white/5 transition-colors disabled:opacity-30">
          <ArrowLeft size={16} /> Back
        </button>
        {step < steps.length - 1 ? (
          <button onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} data-testid="q-next"
            className="flex items-center gap-2 px-8 py-3 bg-gold text-navy font-semibold hover:bg-gold-light transition-colors disabled:opacity-40">
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button onClick={submit} disabled={!canNext || submitting} data-testid="q-submit"
            className="flex items-center gap-2 px-8 py-3 bg-gold text-navy font-semibold hover:bg-gold-light transition-colors disabled:opacity-40">
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Submit Request
          </button>
        )}
      </div>

      {/* Floating concierge toggle — desktop: bottom-right pill; mobile: compact icon under the hamburger (keeps the chat bubble free in the lower-right) */}
      {!aiOpen && (
        <button onClick={() => setAiOpen(true)} data-testid="concierge-fab" aria-label="Open AI concierge"
          className="fixed z-40 flex items-center gap-2 bg-gold text-navy font-semibold shadow-2xl shadow-black/40 hover:bg-gold-light transition-colors
                     top-24 right-6 rounded-full p-3.5
                     md:top-auto md:bottom-28 md:right-6 md:rounded-none md:pl-4 md:pr-5 md:py-3.5">
          <Bot size={18} /> <span className="hidden md:inline">AI Concierge</span>
        </button>
      )}

      {/* Concierge slide-over drawer */}
      <AnimatePresence>
        {aiOpen && (
          <motion.div key="concierge" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center md:p-6" data-testid="concierge-overlay">
            <div onClick={() => setAiOpen(false)} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.aside initial={{ opacity: 0, scale: 0.97, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 16 }} transition={{ type: "tween", duration: 0.25 }}
              className="relative z-10 w-full h-full md:h-[86vh] md:max-w-3xl bg-navy border border-white/10 md:rounded-xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden" data-testid="concierge-drawer">
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10">
                <div className="flex items-center gap-2 text-white">
                  <span className="w-8 h-8 flex items-center justify-center border border-gold/40 text-gold"><Bot size={16} /></span>
                  <span className="font-heading text-lg">AI Concierge</span>
                </div>
                <button onClick={() => setAiOpen(false)} data-testid="concierge-close" aria-label="Close concierge"
                  className="text-gray-400 hover:text-white transition-colors p-1"><X size={20} /></button>
              </div>
              <div className="flex-1 min-h-0 p-3 md:p-5">
                <AiAssistant scope="public" compact mode="assessment" initialSessionId={sessionId} onSessionChange={setSessionId} onQuickSetup={handleQuickSetup} quickSetupBusy={quickBusy} />
              </div>
              <div className="border-t border-white/10 p-3">
                <button onClick={() => applyFromChat()} disabled={extracting || !sessionId} data-testid="concierge-prefill"
                  className="w-full flex items-center justify-center gap-2 bg-gold text-navy py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-40">
                  {extracting ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}
                  Fill my questionnaire from this chat
                </button>
                <p className="text-[11px] text-gray-500 text-center mt-2">I'll read our conversation and fill in what I can — you can review and edit everything.</p>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const inputCls = "w-full bg-navy border border-white/20 text-white p-3 focus:border-gold focus:outline-none";
const provLabel = (src) => !src ? null : (src === "Questionnaire" ? "From Questionnaire" : "From Business Profile");
const DnaChip = ({ src }) => {
  const l = provLabel(src);
  return l ? <span data-testid="q-provenance" className="ml-2 inline-flex items-center gap-1 text-[10px] text-gold/90 align-middle"><Sparkles size={10} /> {l}</span> : null;
};
const StepWrap = ({ title, children }) => (
  <div className="space-y-5"><h2 className="font-heading text-2xl text-white mb-2">{title}</h2>{children}</div>
);
const Text = ({ label, hint, value, onChange, testid, placeholder, type = "text" }) => (
  <div><label className="text-sm text-gray-400 mb-2 block">{label}{hint}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={inputCls} /></div>
);
const Area = ({ label, hint, value, onChange, testid, placeholder }) => (
  <div><label className="text-sm text-gray-400 mb-2 block">{label}{hint}</label>
    <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={inputCls} /></div>
);
const Select = ({ label, value, onChange, testid, options }) => (
  <div><label className="text-sm text-gray-400 mb-2 block">{label}</label>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`px-4 py-2 text-sm border transition-colors ${value === o ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{o}</button>
      ))}
    </div></div>
);
const ChipGroup = ({ label, options, selected, onToggle, testid, max }) => {
  const atMax = max && selected.length >= max;
  return (
  <div><label className="text-sm text-gray-400 mb-2 block flex items-center justify-between">
    <span>{label}</span>
    {max && <span className="text-[11px] text-gray-500">{selected.length}/{max}</span>}
  </label>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = selected.includes(o);
        const disabled = atMax && !active;
        return (
          <button key={o} type="button" onClick={() => { if (!disabled) onToggle(o); }} disabled={disabled}
            className={`px-4 py-2 text-sm border transition-colors flex items-center gap-2 ${active ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}>
            {active && <Check size={13} />} {o}
          </button>
        );
      })}
    </div></div>
  );
};
